<script setup lang="ts">
import { clientsApi, uploadDriversApi } from '@/api/entities'
import type { UploadDriver } from '@/api/entities'
import { isFetchError } from '@/utils/error'
import DynamicForms from '@/components/DynamicForms.vue'
import { getCustomFields } from '@/config/reviewSolutionCustomFields'

definePage({
  meta: {
    title: '신규 고객사 등록',
    action: 'create',
    subject: 'Client',
  },
})

const PRODUCT_ID_PLACEHOLDER = '{product_id}'
const REQUIRED_FIELD_MESSAGE = '필수 입력 항목입니다.'

const router = useRouter()

// 폼 상태
const loading = ref(false)
const formRef = ref()
const serverErrors = ref<Record<string, string[]>>({})

// 업로드 드라이버 목록
const uploadDrivers = ref<UploadDriver[]>([])

// 폼 데이터
const formData = reactive({
  name: '',
  target_database_hive: '',
  host: '',
  m_host: '',
  type: null as string | null,
  widget_enabled: 'Y',
  review_service: null as string | null,
  product_url_pattern: PRODUCT_ID_PLACEHOLDER,
  product_scan_type: 'head',
  product_scan_selector: '',
  score: 4,
  text: 'Y',
  photo: 'Y',
  upload_driver_config: {} as Record<string, unknown>,
})

// 선택된 리뷰 솔루션의 커스텀 필드
const customFields = computed(() => getCustomFields(formData.review_service))

// 이전 솔루션별 설정 임시 저장
const previousConfigs = ref<Record<string, Record<string, unknown>>>({})

// 리뷰 솔루션 변경 시 upload_driver_config 처리
watch(() => formData.review_service, (newService, oldService) => {
  // 이전 설정 백업
  if (oldService && Object.keys(formData.upload_driver_config).length > 0)
    previousConfigs.value[oldService] = { ...formData.upload_driver_config }

  // 새 솔루션의 이전 설정 복원 또는 초기화
  if (newService && previousConfigs.value[newService])
    formData.upload_driver_config = { ...previousConfigs.value[newService] }

  else
    formData.upload_driver_config = {}
})

// 옵션
const typeOptions = [
  { title: '이관', value: 'migration' },
  { title: '위젯', value: 'widget' },
]

const widgetEnabledOptions = [
  { title: '사용', value: 'Y' },
  { title: '미사용', value: 'N' },
]

const productScanTypeOptions = [
  { title: '브라우저 타이틀', value: 'head' },
  { title: '특정 태그', value: 'css' },
]

// 업로드 드라이버 옵션
const uploadDriverOptions = computed(() =>
  uploadDrivers.value
    .filter(driver => driver.driver_state === 'active')
    .map(driver => ({
      title: driver.driver_name,
      value: driver.driver_code,
    })),
)

// 유효성 검사 규칙
const rules = {
  required: [(v: string) => !!v || REQUIRED_FIELD_MESSAGE],
  dbName: [
    (v: string) => !!v || REQUIRED_FIELD_MESSAGE,
    (v: string) => /^[a-z_]+$/.test(v) || '알파벳 소문자와 언더스코어(_)만 입력 가능합니다.',
  ],
  domain: [
    (v: string) => !!v || REQUIRED_FIELD_MESSAGE,
    (v: string) => (!v.startsWith('http') && !v.startsWith('www')) || 'http, www를 제외한 도메인만 입력하세요.',
  ],
  productUrl: [
    (v: string) => !!v || REQUIRED_FIELD_MESSAGE,
    (v: string) => (v.startsWith('http') && v.includes(PRODUCT_ID_PLACEHOLDER)) || `http로 시작하고 ${PRODUCT_ID_PLACEHOLDER}를 포함해야 합니다.`,
  ],
}

// 업로드 드라이버 로드
onMounted(async () => {
  try {
    const response = await uploadDriversApi.listUploadDrivers({ per_page: 100, sort: 'driver_name' })
    if (response.success && response.data) {
      const data = response.data as { data?: UploadDriver[] } | UploadDriver[]

      uploadDrivers.value = Array.isArray(data) ? data : (data.data || [])
    }
  }
  catch (error) {
    console.error('업로드 드라이버 로드 실패:', error)
  }
})

// 422 유효성 검사 에러를 서버 에러 상태에 반영하고 alert 메시지를 표시.
// 422 가 아니거나 error 가 비어있으면 false 를 반환해 호출측에서 기본 실패 alert 을 띄우도록 함
// (원본의 `status === 422 && response.error` 이중 가드와 동치).
const handleValidationError = (response: { status?: number; error?: unknown }): boolean => {
  if (response.status !== 422 || !response.error)
    return false

  const errorData = isFetchError(response.error) ? response.error.response?.data : null
  if (errorData?.errors) {
    serverErrors.value = errorData.errors

    const firstError = Object.values(errorData.errors)[0] as string[] | undefined
    if (firstError && firstError.length > 0) {
      alert(firstError[0])

      return true
    }
  }

  alert(errorData?.message || '입력값을 확인해주세요.')

  return true
}

// 고객사 등록
const createClient = async () => {
  serverErrors.value = {}

  const { valid } = await formRef.value.validate()
  if (!valid)
    return

  const confirmed = await confirm('고객사를 등록하시겠습니까?')
  if (!confirmed)
    return

  loading.value = true
  try {
    const response = await clientsApi.createClient(formData)
    if (response.success) {
      alert('고객사가 등록되었습니다.')
      router.push({ name: 'review-client' })

      return
    }

    if (!handleValidationError(response))
      alert('등록에 실패했습니다.')
  }
  catch (error) {
    console.error('고객사 등록 실패:', error)
    alert('등록 중 오류가 발생했습니다.')
  }
  finally {
    loading.value = false
  }
}

// 확인 다이얼로그
const confirm = (message: string): Promise<boolean> => {
  return Promise.resolve(window.confirm(message))
}
</script>

<template>
  <VForm
    ref="formRef"
    @submit.prevent="createClient"
  >
    <VRow>
      <!-- 왼쪽 컬럼 -->
      <VCol
        cols="12"
        md="6"
      >
        <!-- 기본 정보 -->
        <VCard class="mb-6">
          <VCardTitle>기본 정보</VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="formData.name"
                  label="업체명"
                  :rules="rules.required"
                  required
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="formData.target_database_hive"
                  label="생성 할 DB명"
                  placeholder="예) my_mall_name (알파벳, _ 만 입력 가능)"
                  :rules="rules.dbName"
                  required
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="formData.host"
                  label="PC 도메인"
                  placeholder="예) my-mall.co.kr 또는 shop.mall.co.kr"
                  :rules="rules.domain"
                  required
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="formData.m_host"
                  label="MOBILE 도메인"
                  placeholder="예) mobile-mall.co.kr 또는 m.mall.co.kr"
                  :rules="rules.domain"
                  required
                />
              </VCol>
              <VCol cols="12">
                <VLabel class="mb-2">
                  유형
                </VLabel>
                <VRadioGroup
                  v-model="formData.type"
                  inline
                  :rules="rules.required"
                >
                  <VRadio
                    v-for="option in typeOptions"
                    :key="option.value"
                    :label="option.title"
                    :value="option.value"
                  />
                </VRadioGroup>
              </VCol>
              <VCol
                v-if="formData.type === 'widget'"
                cols="12"
              >
                <VLabel class="mb-2">
                  위젯 상태
                </VLabel>
                <VRadioGroup
                  v-model="formData.widget_enabled"
                  inline
                >
                  <VRadio
                    v-for="option in widgetEnabledOptions"
                    :key="option.value"
                    :label="option.title"
                    :value="option.value"
                  />
                </VRadioGroup>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- 자사몰 정보 -->
        <VCard>
          <VCardTitle>자사몰 정보</VCardTitle>
          <VCardText>
            <h6 class="text-h6 mb-4">
              리뷰 솔루션
            </h6>
            <VRow>
              <VCol cols="12">
                <AppSelect
                  v-model="formData.review_service"
                  label="솔루션"
                  :items="uploadDriverOptions"
                  :rules="rules.required"
                  required
                />
              </VCol>
            </VRow>

            <!-- 솔루션별 추가 설정 -->
            <template v-if="customFields.length > 0">
              <VDivider class="my-6" />
              <h6 class="text-h6 mb-4">
                솔루션 추가 설정
              </h6>
              <DynamicForms
                v-model="formData.upload_driver_config"
                :fields="customFields"
              />
            </template>

            <VDivider class="my-6" />

            <h6 class="text-h6 mb-4">
              상품 스캔
            </h6>
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="formData.product_url_pattern"
                  label="상세페이지 URL 패턴"
                  placeholder="예) https://mall.co.kr/product/detail.html?product_no={product_id}"
                  :rules="rules.productUrl"
                  required
                />
              </VCol>
              <VCol cols="12">
                <VLabel class="mb-2">
                  상품명 스캔 위치
                </VLabel>
                <VRadioGroup
                  v-model="formData.product_scan_type"
                  inline
                >
                  <VRadio
                    v-for="option in productScanTypeOptions"
                    :key="option.value"
                    :label="option.title"
                    :value="option.value"
                  />
                </VRadioGroup>
              </VCol>
              <VCol
                v-if="formData.product_scan_type === 'css'"
                cols="12"
              >
                <AppTextField
                  v-model="formData.product_scan_selector"
                  label="상품명 태그 셀렉터"
                  :rules="formData.product_scan_type === 'css' ? rules.required : []"
                  :required="formData.product_scan_type === 'css'"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>

      <!-- 오른쪽 컬럼 -->
      <VCol
        cols="12"
        md="6"
      >
        <!-- 이관 대상 -->
        <VCard class="mb-6">
          <VCardTitle>이관 대상</VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12">
                <VLabel class="mb-2">
                  별점
                </VLabel>
                <VRating
                  v-model="formData.score"
                  color="warning"
                  active-color="warning"
                />
              </VCol>
              <VCol cols="12">
                <div class="d-flex align-center justify-space-between">
                  <VLabel>텍스트 리뷰 이관</VLabel>
                  <VSwitch
                    v-model="formData.text"
                    true-value="Y"
                    false-value="N"
                    inset
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="d-flex align-center justify-space-between">
                  <VLabel>포토 리뷰 이관</VLabel>
                  <VSwitch
                    v-model="formData.photo"
                    true-value="Y"
                    false-value="N"
                    inset
                  />
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 버튼 -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex gap-4">
          <VBtn
            type="submit"
            color="primary"
            :loading="loading"
          >
            고객사 등록
          </VBtn>
          <VBtn
            variant="outlined"
            :to="{ name: 'review-client' }"
          >
            취소
          </VBtn>
        </div>
      </VCol>
    </VRow>
  </VForm>
</template>
